import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchSubscriptionTracker,
  fetchTeamSubscriptionSummaries,
  orderedUniqueTeamIds,
} from '../../../services/admin/subscriptionTrackerApi';
import {
  SubscriptionTrackerListResponse,
  SubscriptionTrackerUser,
  TeamSubscriptionSummary,
} from '../../../types/admin/subscriptionTracker';
import { useAbortableRequest } from '../../../hooks/useAbortableRequest';
import Spinner from '../../Spinner';
import UserSubFilterBar, { UserSubTrialPreset } from './UserSubFilterBar';
import UserSubTable from './UserSubTable';
import UserSubPagination from './UserSubPagination';
import UserViewPlanModal from './UserViewPlanModal';
import {
  defaultEndingSoonDateInputs,
  resolveEndingSoonRange,
} from '../../../utils/admin/formatSubscriptionDate';

const TEAM_SUMMARY_BATCH_SIZE = 5;
/** Above-the-fold team headers only; rest load on scroll. */
const TEAM_SUMMARY_PREFETCH = 2;
const TEAM_SUMMARY_QUEUE_DEBOUNCE_MS = 50;

function hasTeamSummary(
  map: Record<string, TeamSubscriptionSummary>,
  teamId: number
): boolean {
  return map[String(teamId)] != null || (map as Record<number, TeamSubscriptionSummary>)[teamId] != null;
}

const UserSubPage: React.FC = () => {
  const navigate = useNavigate();
  const { start: startListRequest } = useAbortableRequest();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(50);
  const [trialPreset, setTrialPreset] = useState<UserSubTrialPreset>('all');
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [endsFrom, setEndsFrom] = useState(() => defaultEndingSoonDateInputs().from);
  const [endsTo, setEndsTo] = useState(() => defaultEndingSoonDateInputs().to);
  const [sportCode, setSportCode] = useState('');

  const [listData, setListData] = useState<SubscriptionTrackerListResponse | null>(null);
  const [teamSummaries, setTeamSummaries] = useState<Record<string, TeamSubscriptionSummary>>({});
  const [loadingTeamIds, setLoadingTeamIds] = useState<Record<number, boolean>>({});
  const [pendingTeamIds, setPendingTeamIds] = useState<Record<number, boolean>>({});
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [summariesError, setSummariesError] = useState<string | null>(null);

  const [planUser, setPlanUser] = useState<SubscriptionTrackerUser | null>(null);

  const teamSummariesRef = useRef(teamSummaries);
  teamSummariesRef.current = teamSummaries;

  const summaryQueueRef = useRef<number[]>([]);
  const inFlightTeamIdsRef = useRef<Set<number>>(new Set());
  const queueTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const drainRunningRef = useRef(false);
  const summaryAbortRef = useRef<AbortController | null>(null);
  const listRequestGenRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setOffset(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const abortSummaryRequests = useCallback(() => {
    summaryAbortRef.current?.abort();
    summaryAbortRef.current = null;
  }, []);

  const getSummaryAbortSignal = useCallback((): AbortSignal => {
    if (summaryAbortRef.current == null || summaryAbortRef.current.signal.aborted) {
      summaryAbortRef.current = new AbortController();
    }
    return summaryAbortRef.current.signal;
  }, []);

  const markTeamsLoading = useCallback((teamIds: number[], loading: boolean) => {
    if (teamIds.length === 0) return;
    setLoadingTeamIds((prev) => {
      const next = { ...prev };
      for (const tid of teamIds) {
        if (loading) next[tid] = true;
        else delete next[tid];
      }
      return next;
    });
  }, []);

  const markTeamsPending = useCallback((teamIds: number[]) => {
    if (teamIds.length === 0) return;
    setPendingTeamIds((prev) => {
      const next = { ...prev };
      for (const tid of teamIds) {
        next[tid] = true;
      }
      return next;
    });
  }, []);

  const clearTeamsPending = useCallback((teamIds: number[]) => {
    if (teamIds.length === 0) return;
    setPendingTeamIds((prev) => {
      const next = { ...prev };
      for (const tid of teamIds) {
        delete next[tid];
      }
      return next;
    });
  }, []);

  const applyTeamSummaryBatch = useCallback(
    (batch: Record<string, TeamSubscriptionSummary>, loadedTeamIds: number[]) => {
      if (batch && Object.keys(batch).length > 0) {
        setTeamSummaries((prev) => ({ ...prev, ...batch }));
      }
      if (loadedTeamIds.length > 0) {
        setLoadingTeamIds((prev) => {
          const next = { ...prev };
          for (const tid of loadedTeamIds) {
            delete next[tid];
          }
          return next;
        });
        setPendingTeamIds((prev) => {
          const next = { ...prev };
          for (const tid of loadedTeamIds) {
            delete next[tid];
          }
          return next;
        });
      }
    },
    []
  );

  const pullSummaryBatch = useCallback((): number[] => {
    const batch: number[] = [];
    while (batch.length < TEAM_SUMMARY_BATCH_SIZE && summaryQueueRef.current.length > 0) {
      const tid = summaryQueueRef.current.shift()!;
      if (hasTeamSummary(teamSummariesRef.current, tid)) continue;
      if (inFlightTeamIdsRef.current.has(tid)) continue;
      batch.push(tid);
    }
    return batch;
  }, []);

  const runSummaryDrainLoop = useCallback(async () => {
    if (drainRunningRef.current) return;
    drainRunningRef.current = true;

    try {
      for (;;) {
        const batch = pullSummaryBatch();
        if (batch.length === 0) break;

        for (const tid of batch) {
          inFlightTeamIdsRef.current.add(tid);
        }
        markTeamsLoading(batch, true);

        const signal = getSummaryAbortSignal();
        const result = await fetchTeamSubscriptionSummaries(batch, {
          includeActivatedBy: false,
          signal,
        });

        for (const tid of batch) {
          inFlightTeamIdsRef.current.delete(tid);
        }

        if (result.aborted || signal.aborted) {
          markTeamsLoading(batch, false);
          for (const tid of batch) {
            if (!hasTeamSummary(teamSummariesRef.current, tid)) {
              if (!summaryQueueRef.current.includes(tid)) {
                summaryQueueRef.current.push(tid);
              }
            }
          }
          break;
        }

        if (result.data?.team_summaries) {
          applyTeamSummaryBatch(result.data.team_summaries, batch);
        } else {
          markTeamsLoading(batch, false);
          clearTeamsPending(batch);
          if (result.error) {
            setSummariesError(result.error);
          }
        }
      }
    } finally {
      drainRunningRef.current = false;
      if (summaryQueueRef.current.length > 0) {
        void runSummaryDrainLoop();
      }
    }
  }, [
    applyTeamSummaryBatch,
    clearTeamsPending,
    getSummaryAbortSignal,
    markTeamsLoading,
    pullSummaryBatch,
  ]);

  const scheduleSummaryDrain = useCallback(() => {
    if (queueTimerRef.current != null) {
      clearTimeout(queueTimerRef.current);
    }
    queueTimerRef.current = setTimeout(() => {
      queueTimerRef.current = null;
      void runSummaryDrainLoop();
    }, TEAM_SUMMARY_QUEUE_DEBOUNCE_MS);
  }, [runSummaryDrainLoop]);

  const enqueueTeamSummaryRequests = useCallback(
    (teamIds: number[], options?: { immediate?: boolean }) => {
      const map = teamSummariesRef.current;
      const newlyRequested: number[] = [];
      for (const tid of teamIds) {
        if (!Number.isFinite(tid) || tid <= 0) continue;
        if (hasTeamSummary(map, tid)) continue;
        if (inFlightTeamIdsRef.current.has(tid)) continue;
        if (summaryQueueRef.current.includes(tid)) continue;
        summaryQueueRef.current.push(tid);
        newlyRequested.push(tid);
      }
      if (newlyRequested.length > 0) {
        markTeamsPending(newlyRequested);
        if (options?.immediate) {
          void runSummaryDrainLoop();
        } else {
          scheduleSummaryDrain();
        }
      }
    },
    [markTeamsPending, runSummaryDrainLoop, scheduleSummaryDrain]
  );

  const onRequestTeamSummary = useCallback(
    (teamId: number) => {
      enqueueTeamSummaryRequests([teamId]);
    },
    [enqueueTeamSummaryRequests]
  );

  const resetSummaryState = useCallback(() => {
    abortSummaryRequests();
    summaryQueueRef.current = [];
    inFlightTeamIdsRef.current.clear();
    drainRunningRef.current = false;
    if (queueTimerRef.current != null) {
      clearTimeout(queueTimerRef.current);
      queueTimerRef.current = null;
    }
    setTeamSummaries({});
    setLoadingTeamIds({});
    setPendingTeamIds({});
  }, [abortSummaryRequests]);

  const prefetchTeamSummaries = useCallback(
    (users: SubscriptionTrackerUser[]) => {
      const firstIds = orderedUniqueTeamIds(users).slice(0, TEAM_SUMMARY_PREFETCH);
      enqueueTeamSummaryRequests(firstIds, { immediate: true });
    },
    [enqueueTeamSummaryRequests]
  );

  const loadList = useCallback(async () => {
    const requestGen = ++listRequestGenRef.current;
    const listController = startListRequest();

    setListLoading(true);
    setListError(null);
    setSummariesError(null);

    const endingSoonRange =
      trialPreset === 'trial_ending_soon' ||
      trialPreset === 'grace_ending_soon' ||
      trialPreset === 'sub_ending_soon'
        ? resolveEndingSoonRange({ useCustomRange, endsFrom, endsTo })
        : null;

    if (
      (trialPreset === 'trial_ending_soon' ||
        trialPreset === 'grace_ending_soon' ||
        trialPreset === 'sub_ending_soon') &&
      endingSoonRange == null
    ) {
      setListLoading(false);
      return;
    }

    const trialParams =
      trialPreset === 'trial_ending_soon' && endingSoonRange
        ? {
            trial_status: 'trial' as const,
            trial_end_from: endingSoonRange.from,
            trial_end_to: endingSoonRange.to,
          }
        : trialPreset === 'trial_expired'
          ? { trial_status: 'trial_end' as const }
          : {};

    const subscriptionEndRange =
      (trialPreset === 'grace_ending_soon' || trialPreset === 'sub_ending_soon') &&
      endingSoonRange
        ? {
            subscription_end_from: endingSoonRange.from,
            subscription_end_to: endingSoonRange.to,
          }
        : {};

    const subscriptionStatusParam =
      trialPreset === 'grace_ending_soon'
        ? { subscription_status: 'grace' as const }
        : trialPreset === 'grace_ended'
          ? { subscription_status: 'grace_ended' as const }
          : trialPreset === 'sub_ending_soon'
            ? { subscription_status: 'subscribed' as const }
            : trialPreset === 'ended'
              ? { subscription_status: 'ended' as const }
              : {};

    const result = await fetchSubscriptionTracker(
      {
        search: debouncedSearch || undefined,
        limit,
        offset,
        includeTeamSummaries: false,
        sport_code: sportCode || undefined,
        ...trialParams,
        ...subscriptionStatusParam,
        ...subscriptionEndRange,
      },
      listController.signal
    );

    if (requestGen !== listRequestGenRef.current) {
      return;
    }

    if (result.aborted || listController.signal.aborted) {
      setListLoading(false);
      return;
    }

    if (result.status === 401 || result.status === 403) {
      setListLoading(false);
      navigate('/dashboard');
      return;
    }

    if (result.data) {
      resetSummaryState();
      setListData(result.data);
      setListLoading(false);
      prefetchTeamSummaries(result.data.users ?? []);
    } else {
      setListData(null);
      setListError(result.error ?? 'Failed to load users');
      setListLoading(false);
    }
  }, [
    debouncedSearch,
    endsFrom,
    endsTo,
    limit,
    offset,
    navigate,
    prefetchTeamSummaries,
    resetSummaryState,
    sportCode,
    startListRequest,
    trialPreset,
    useCustomRange,
  ]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => () => abortSummaryRequests(), [abortSummaryRequests]);

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setOffset(0);
  };

  const handlePresetChange = (value: UserSubTrialPreset) => {
    setListLoading(true);
    setTrialPreset(value);
    if (
      value !== 'trial_ending_soon' &&
      value !== 'grace_ending_soon' &&
      value !== 'sub_ending_soon'
    ) {
      setUseCustomRange(false);
    }
    setOffset(0);
  };

  const handleUseCustomRangeChange = (value: boolean) => {
    setListLoading(true);
    setUseCustomRange(value);
    if (value) {
      const defaults = defaultEndingSoonDateInputs();
      setEndsFrom(defaults.from);
      setEndsTo(defaults.to);
    }
    setOffset(0);
  };

  const handleEndsFromChange = (value: string) => {
    setListLoading(true);
    setEndsFrom(value);
    setOffset(0);
  };

  const handleEndsToChange = (value: string) => {
    setListLoading(true);
    setEndsTo(value);
    setOffset(0);
  };

  const handleSportCodeChange = (value: string) => {
    setListLoading(true);
    setSportCode(value);
    setOffset(0);
  };

  const showUsersTable = listData != null && !listLoading;
  const isListRefreshing = listLoading;

  return (
    <div className="p-4 bg-white shadow-md rounded-md mb-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">User Subscriptions</h2>
          <p className="text-gray-600 text-sm mt-1">
            Team subscription under each team name. Use View Plan for that user’s subscription.
          </p>
        </div>
        {listData?.pagination && !listLoading && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-4 py-3 min-w-[120px]">
            <p className="text-xs font-bold text-gray-500 uppercase mb-1">User Sub</p>
            <p className="text-2xl font-semibold text-gray-800">{listData.pagination.total}</p>
          </div>
        )}
      </div>

      <UserSubFilterBar
        preset={trialPreset}
        onPresetChange={handlePresetChange}
        useCustomRange={useCustomRange}
        onUseCustomRangeChange={handleUseCustomRangeChange}
        endsFrom={endsFrom}
        endsTo={endsTo}
        onEndsFromChange={handleEndsFromChange}
        onEndsToChange={handleEndsToChange}
        sportCode={sportCode}
        onSportCodeChange={handleSportCodeChange}
        search={search}
        onSearchChange={setSearch}
        onRefresh={loadList}
        listLoading={listLoading}
      />

      {listError && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {listError}
        </div>
      )}

      {summariesError && !listError && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-300 text-amber-900 rounded text-sm">
          {summariesError}
        </div>
      )}

      {isListRefreshing && (
        <div className="flex justify-center items-center h-40 text-sm text-gray-600">
          <Spinner size="md" />
          <span className="ml-2">Loading users…</span>
        </div>
      )}

      {showUsersTable && (
        <>
          <UserSubTable
            users={listData.users ?? []}
            teamSummaries={teamSummaries}
            loadingTeamIds={loadingTeamIds}
            pendingTeamIds={pendingTeamIds}
            onRequestTeamSummary={onRequestTeamSummary}
            onViewPlan={setPlanUser}
          />
          {listData.pagination && (
            <UserSubPagination
              pagination={listData.pagination}
              onOffsetChange={setOffset}
              onLimitChange={handleLimitChange}
            />
          )}
        </>
      )}

      <UserViewPlanModal
        isOpen={planUser != null}
        userId={planUser?.user_id ?? null}
        username={planUser?.username ?? ''}
        email={planUser?.email ?? ''}
        teamId={planUser?.team_id ?? null}
        allowAll={false}
        mode="user"
        onClose={() => setPlanUser(null)}
      />
    </div>
  );
};

export default UserSubPage;
